import React from 'react';
import axios from 'axios';
import Modal from './Modal';
import { getAuthHeaders, urlMap } from '../../utils';

const STEP_CONFIRM = 'confirm';   // user types DELETE
const STEP_PASSWORD = 'password'; // user enters password
const STEP_DONE = 'done';

export default class DeleteAccountModal extends React.Component {
  state = {
    step: STEP_CONFIRM,
    confirmText: '',
    password: '',
    confirmToken: null,
    error: null,
    submitting: false,
  };

  handleClose = () => {
    if (this.state.submitting) return;
    this.setState({
      step: STEP_CONFIRM,
      confirmText: '',
      password: '',
      confirmToken: null,
      error: null,
      submitting: false,
    });
    this.props.onClose();
  };

  handleConfirmTextChange = (e) => {
    this.setState({ confirmText: e.target.value, error: null });
  };

  handlePasswordChange = (e) => {
    this.setState({ password: e.target.value, error: null });
  };

  handleRequestDeletion = async () => {
    this.setState({ submitting: true, error: null });
    try {
      const { data } = await axios.post(
        urlMap.accountDeletionRequest,
        {},
        { headers: getAuthHeaders() }
      );
      this.setState({ confirmToken: data.confirmToken, step: STEP_PASSWORD, submitting: false });
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        status === 429
          ? 'Too many attempts. Please wait an hour before trying again.'
          : 'Something went wrong. Please try again.';
      this.setState({ error: msg, submitting: false });
    }
  };

  handleConfirmDeletion = async () => {
    const { confirmToken, password } = this.state;
    this.setState({ submitting: true, error: null });
    try {
      await axios.post(
        urlMap.accountDeletionConfirm,
        { confirmToken, password },
        { headers: getAuthHeaders() }
      );
      this.setState({ step: STEP_DONE, submitting: false });
      // Sign out and redirect after a short pause so the user reads the done screen
      setTimeout(() => {
        try { localStorage.removeItem('token'); } catch {}
        window.location.href = '/';
      }, 3000);
    } catch (err) {
      const status = err?.response?.status;
      let msg = 'Something went wrong. Please try again.';
      if (status === 401) msg = 'Incorrect password.';
      if (status === 400) msg = 'The confirmation window expired. Please start over.';
      if (status === 429) msg = 'Too many attempts. Please wait an hour before trying again.';
      this.setState({ error: msg, submitting: false });
    }
  };

  renderConfirmStep() {
    const { confirmText, error, submitting } = this.state;
    const canContinue = confirmText === 'DELETE';

    return (
      <>
        <h2 className="da-modal-title">Delete your account?</h2>
        <p className="da-modal-body">
          This will permanently delete your account, all your projects, chapters, and analysis
          history. <strong>This cannot be undone.</strong>
        </p>
        <p className="da-modal-body">
          Any remaining fig coins will be permanently forfeited when your account is deleted.
        </p>

        <div className="da-confirm-input-group">
          <label htmlFor="da-confirm-text" className="da-label">
            Type <strong>DELETE</strong> to continue
          </label>
          <input
            id="da-confirm-text"
            className="da-input"
            type="text"
            autoComplete="off"
            value={confirmText}
            onChange={this.handleConfirmTextChange}
            placeholder="DELETE"
          />
        </div>

        {error && <p className="da-error">{error}</p>}

        <div className="da-actions">
          <button
            type="button"
            className="da-btn da-btn--ghost"
            onClick={this.handleClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="da-btn da-btn--danger"
            onClick={this.handleRequestDeletion}
            disabled={!canContinue || submitting}
          >
            {submitting ? 'Please wait…' : 'Continue'}
          </button>
        </div>
      </>
    );
  }

  renderPasswordStep() {
    const { password, error, submitting } = this.state;

    return (
      <>
        <h2 className="da-modal-title">Confirm with your password</h2>
        <p className="da-modal-body">
          Enter your password to permanently delete your account. You have 15 minutes to complete
          this step.
        </p>

        <div className="da-confirm-input-group">
          <label htmlFor="da-password" className="da-label">Password</label>
          <input
            id="da-password"
            className="da-input"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={this.handlePasswordChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && password && !submitting) this.handleConfirmDeletion();
            }}
          />
        </div>

        {error && <p className="da-error">{error}</p>}

        <div className="da-actions">
          <button
            type="button"
            className="da-btn da-btn--ghost"
            onClick={this.handleClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="da-btn da-btn--danger"
            onClick={this.handleConfirmDeletion}
            disabled={!password || submitting}
          >
            {submitting ? 'Deleting…' : 'Delete my account'}
          </button>
        </div>
      </>
    );
  }

  renderDoneStep() {
    return (
      <>
        <h2 className="da-modal-title">Account deleted</h2>
        <p className="da-modal-body">
          Your account and all associated content have been permanently deleted. Redirecting you
          now…
        </p>
      </>
    );
  }

  render() {
    const { isOpen } = this.props;
    const { step } = this.state;

    return (
      <Modal isOpen={isOpen} onClose={step === STEP_DONE ? undefined : this.handleClose}>
        <div className="da-modal">
          {step === STEP_CONFIRM && this.renderConfirmStep()}
          {step === STEP_PASSWORD && this.renderPasswordStep()}
          {step === STEP_DONE && this.renderDoneStep()}
        </div>
      </Modal>
    );
  }
}
